import { useEffect } from "react";
import { io, type Socket } from "socket.io-client";
import { useClinicianAuthStore } from "@/stores/clinicianAuthStore";
import { mocksEnabled } from "@/portals/clinician/api/transport";
import { hardenSocket } from "@/lib/socketRecovery";

const BASE_URL = import.meta.env.VITE_API_URL ?? "";

// Separate from socketClient.ts's patient socket on purpose: that one is bound
// to the patient store (and currently switched off). `forceNew` keeps this on
// its own Manager/connection, so the two can never share a handshake token.
// The server joins it to user:{userId} and clinicians. Module 4 listens for
// review_queue:item_added / item_removed (see useClinicianLiveQueries) and
// notification:new (see clinicianNotificationsStore).
let socket: Socket | null = null;

export function getClinicianSocket(): Socket {
  if (socket) return socket;
  socket = io(BASE_URL || undefined, {
    autoConnect: false,
    forceNew: true,
    withCredentials: true,
    // WebSocket only — see socketClient.ts for why polling breaks on the
    // multi-worker backend.
    transports: ["websocket"],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10_000,
  });
  socket.on("connect", () => console.info("[realtime:clinician] connected", socket?.id));
  socket.on("disconnect", (reason) => console.warn("[realtime:clinician] disconnected:", reason));
  socket.on("connect_error", (err) => console.error("[realtime:clinician] failed to connect:", err.message));
  hardenSocket(socket, () => Boolean(useClinicianAuthStore.getState().accessToken));
  return socket;
}

export function syncClinicianSocket(token: string | null): void {
  if (mocksEnabled()) return; // no Socket.IO server behind the mocks
  const s = getClinicianSocket();
  if (!token) {
    s.disconnect();
    return;
  }
  // Applies to the next (re)connect. The contract names no in-place re-auth
  // event for clinicians, so a live connection keeps its handshake token.
  s.auth = { token };
  if (!s.connected) s.connect();
}

/** Mounted by the protected clinician layout: connects after login, disconnects on logout/unmount. */
export function useClinicianSocket(): void {
  const token = useClinicianAuthStore((s) => s.accessToken);
  useEffect(() => {
    syncClinicianSocket(token);
  }, [token]);
  useEffect(() => () => syncClinicianSocket(null), []);
}
